import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useEstablishment } from '@/contexts/EstablishmentContext';

export interface CustomerNoteRecord {
  id: string;
  email: string;
  notes: string | null;
  isBlacklisted?: boolean;
  hasNoShow?: boolean;
  allergiesNotes?: string | null;
  allergiesTags?: string[];
  guestTags?: string[];
}

export function useCustomerNotes() {
  const { establishmentId } = useEstablishment();
  const [customerNotes, setCustomerNotes] = useState<Map<string, CustomerNoteRecord>>(new Map());
  const [loading, setLoading] = useState(true);

  const fetchCustomerNotes = useCallback(async () => {
    if (!establishmentId) {
      setLoading(false);
      return;
    }

    try {
      const notesMap = new Map<string, CustomerNoteRecord>();
      const PAGE_SIZE = 1000;
      let from = 0;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from('customers')
          .select('id, email, notes, is_blacklisted, has_no_show, allergies_notes, allergies_tags, guest_tags')
          .eq('establishment_id', establishmentId)
          .range(from, from + PAGE_SIZE - 1);

        if (error) {
          console.error('[useCustomerNotes] Error fetching notes:', error);
          return;
        }

        if (!data || data.length === 0) {
          hasMore = false;
          break;
        }

        data.forEach(customer => {
          if (customer.email) {
            notesMap.set(customer.email.toLowerCase(), {
              id: customer.id,
              email: customer.email,
              notes: customer.notes,
              isBlacklisted: customer.is_blacklisted ?? false,
              hasNoShow: customer.has_no_show ?? false,
              allergiesNotes: customer.allergies_notes,
              allergiesTags: customer.allergies_tags ?? [],
              guestTags: customer.guest_tags ?? [],
            });
          }
        });

        from += PAGE_SIZE;
        if (data.length < PAGE_SIZE) {
          hasMore = false;
        }
      }

      setCustomerNotes(notesMap);
    } catch (err) {
      console.error('[useCustomerNotes] Exception:', err);
    } finally {
      setLoading(false);
    }
  }, [establishmentId]);

  useEffect(() => {
    fetchCustomerNotes();
  }, [fetchCustomerNotes]);

  // Helper to get notes for a specific email
  const getNotesForEmail = useCallback((email: string | undefined): string | undefined => {
    if (!email) return undefined;
    return customerNotes.get(email.toLowerCase())?.notes || undefined;
  }, [customerNotes]);

  // Helper to get customer ID for a specific email
  const getCustomerIdForEmail = useCallback((email: string | undefined): string | undefined => {
    if (!email) return undefined;
    return customerNotes.get(email.toLowerCase())?.id;
  }, [customerNotes]);

  return {
    customerNotes,
    loading,
    refetch: fetchCustomerNotes,
    getNotesForEmail,
    getCustomerIdForEmail,
  };
}
